/**
 * PATH: apps/hr/src/pages/employees/employee-detail/EditModals.tsx
 * PURPOSE: Edit modals for employment, contact, emergency, bank, and personal info
 */

import { useState } from 'react'
import { Briefcase, Mail, AlertCircle, CreditCard, UserCircle } from 'lucide-react'
import type { Employee } from '../../../types'
import { EditModal, FormInput, FormSelect } from './detail-shared-ui'

// =============================================================================
// EDIT EMPLOYMENT MODAL
// =============================================================================

export function EditEmploymentModal({ isOpen, onClose, employee, onSave, isSaving }: { isOpen: boolean; onClose: () => void; employee: Employee; onSave: (data: Partial<Employee>) => void; isSaving?: boolean }) {
  const [department, setDepartment] = useState(employee.department)
  const [position, setPosition] = useState(employee.position)
  const [employmentType, setEmploymentType] = useState(employee.employmentType)
  const [hireDate, setHireDate] = useState(employee.hireDate || '')
  const [location, setLocation] = useState(employee.location || '')
  const [managerName, setManagerName] = useState(employee.managerName || '')
  const opts = [{ value: 'full_time', label: 'Full Time' }, { value: 'part_time', label: 'Part Time' }, { value: 'casual', label: 'Casual' }, { value: 'contractor', label: 'Contractor' }]
  return (
    <EditModal isOpen={isOpen} onClose={onClose} title="Edit Employment Information" icon={Briefcase} onSave={() => onSave({ department, position, employmentType, hireDate, location, managerName })} isSaving={isSaving}>
      <div className="space-y-4">
        <FormInput label="Department" value={department} onChange={setDepartment} placeholder="e.g., Engineering" required />
        <FormInput label="Position" value={position} onChange={setPosition} placeholder="e.g., Senior Developer" required />
        <FormSelect label="Employment Type" value={employmentType} onChange={(v) => setEmploymentType(v as Employee['employmentType'])} options={opts} required />
        <FormInput label="Hire Date" value={hireDate} onChange={setHireDate} type="date" />
        <FormInput label="Location" value={location} onChange={setLocation} placeholder="e.g., Sydney" />
        <FormInput label="Manager" value={managerName} onChange={setManagerName} placeholder="Manager name" />
      </div>
    </EditModal>
  )
}

// =============================================================================
// EDIT CONTACT MODAL
// =============================================================================

export function EditContactModal({ isOpen, onClose, employee, onSave, isSaving }: { isOpen: boolean; onClose: () => void; employee: Employee; onSave: (data: Partial<Employee>) => void; isSaving?: boolean }) {
  const [email, setEmail] = useState(employee.email || '')
  const [phone, setPhone] = useState(employee.phone || '')
  const [addressLine1, setAddressLine1] = useState(employee.address?.line1 || '')
  const [addressLine2, setAddressLine2] = useState(employee.address?.line2 || '')
  const [city, setCity] = useState(employee.address?.city || '')
  const [state, setState] = useState(employee.address?.state || '')
  const [postalCode, setPostalCode] = useState(employee.address?.postalCode || '')
  const stateOpts = [{ value: 'NSW', label: 'New South Wales' }, { value: 'VIC', label: 'Victoria' }, { value: 'QLD', label: 'Queensland' }, { value: 'WA', label: 'Western Australia' }, { value: 'SA', label: 'South Australia' }, { value: 'TAS', label: 'Tasmania' }, { value: 'ACT', label: 'ACT' }, { value: 'NT', label: 'Northern Territory' }]
  const handleSave = () => onSave({ email, phone, address: addressLine1 ? { line1: addressLine1, line2: addressLine2 || undefined, city, state, postalCode, country: 'Australia' } : undefined })
  return (
    <EditModal isOpen={isOpen} onClose={onClose} title="Edit Contact Information" icon={Mail} onSave={handleSave} isSaving={isSaving}>
      <div className="space-y-4">
        <FormInput label="Email" value={email} onChange={setEmail} type="email" placeholder="email@example.com" required />
        <FormInput label="Phone" value={phone} onChange={setPhone} type="tel" placeholder="+61 400 000 000" />
        <div className="pt-2 border-t border-slate-200 dark:border-slate-700">
          <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">Address</p>
          <div className="space-y-3">
            <FormInput label="Street Address" value={addressLine1} onChange={setAddressLine1} placeholder="123 Main Street" />
            <FormInput label="Address Line 2" value={addressLine2} onChange={setAddressLine2} placeholder="Unit 4 (optional)" />
            <div className="grid grid-cols-2 gap-3">
              <FormInput label="City" value={city} onChange={setCity} placeholder="Sydney" />
              <FormSelect label="State" value={state} onChange={setState} options={stateOpts} placeholder="Select state" />
            </div>
            <FormInput label="Postcode" value={postalCode} onChange={setPostalCode} placeholder="2000" />
          </div>
        </div>
      </div>
    </EditModal>
  )
}

// =============================================================================
// EDIT EMERGENCY CONTACT MODAL
// =============================================================================

export function EditEmergencyContactModal({ isOpen, onClose, employee, onSave, isSaving }: { isOpen: boolean; onClose: () => void; employee: Employee; onSave: (data: Partial<Employee>) => void; isSaving?: boolean }) {
  const [name, setName] = useState(employee.emergencyContact?.name || '')
  const [relationship, setRelationship] = useState(employee.emergencyContact?.relationship || '')
  const [phone, setPhone] = useState(employee.emergencyContact?.phone || '')
  const [email, setEmail] = useState(employee.emergencyContact?.email || '')
  const relOpts = [{ value: 'Spouse', label: 'Spouse' }, { value: 'Partner', label: 'Partner' }, { value: 'Parent', label: 'Parent' }, { value: 'Sibling', label: 'Sibling' }, { value: 'Child', label: 'Child' }, { value: 'Friend', label: 'Friend' }, { value: 'Other', label: 'Other' }]
  return (
    <EditModal isOpen={isOpen} onClose={onClose} title="Edit Emergency Contact" icon={AlertCircle} onSave={() => onSave({ emergencyContact: name ? { name, relationship, phone, email: email || undefined } : undefined })} isSaving={isSaving}>
      <div className="space-y-4">
        <FormInput label="Contact Name" value={name} onChange={setName} placeholder="Full name" required />
        <FormSelect label="Relationship" value={relationship} onChange={setRelationship} options={relOpts} placeholder="Select relationship" required />
        <FormInput label="Phone Number" value={phone} onChange={setPhone} type="tel" placeholder="+61 400 000 000" required />
        <FormInput label="Email (optional)" value={email} onChange={setEmail} type="email" placeholder="email@example.com" />
      </div>
    </EditModal>
  )
}

// =============================================================================
// EDIT BANK DETAILS MODAL
// =============================================================================

export function EditBankDetailsModal({ isOpen, onClose, employee, onSave, isSaving }: { isOpen: boolean; onClose: () => void; employee: Employee; onSave: (data: Partial<Employee>) => void; isSaving?: boolean }) {
  const [accountName, setAccountName] = useState(employee.bankDetails?.accountName || '')
  const [bsb, setBsb] = useState(employee.bankDetails?.bsb || '')
  const [accountNumber, setAccountNumber] = useState(employee.bankDetails?.accountNumber || '')
  const [bankName, setBankName] = useState(employee.bankDetails?.bankName || '')
  const bankOpts = [{ value: 'Commonwealth Bank', label: 'Commonwealth Bank' }, { value: 'Westpac', label: 'Westpac' }, { value: 'NAB', label: 'NAB' }, { value: 'ANZ', label: 'ANZ' }, { value: 'Macquarie Bank', label: 'Macquarie Bank' }, { value: 'Bendigo Bank', label: 'Bendigo Bank' }, { value: 'Bank of Queensland', label: 'Bank of Queensland' }, { value: 'Suncorp', label: 'Suncorp' }, { value: 'ING', label: 'ING' }, { value: 'Other', label: 'Other' }]
  return (
    <EditModal isOpen={isOpen} onClose={onClose} title="Edit Bank Details" icon={CreditCard} onSave={() => onSave({ bankDetails: accountName && bsb && accountNumber ? { accountName, bsb, accountNumber, bankName: bankName || undefined } : undefined })} isSaving={isSaving}>
      <div className="space-y-4">
        <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
          <p className="text-xs text-amber-700 dark:text-amber-400">Bank details are sensitive information. Please ensure the employee has authorised these changes.</p>
        </div>
        <FormInput label="Account Name" value={accountName} onChange={setAccountName} placeholder="Name on account" required />
        <div className="grid grid-cols-2 gap-3">
          <FormInput label="BSB" value={bsb} onChange={setBsb} placeholder="000-000" required />
          <FormInput label="Account Number" value={accountNumber} onChange={setAccountNumber} placeholder="12345678" required />
        </div>
        <FormSelect label="Bank" value={bankName} onChange={setBankName} options={bankOpts} placeholder="Select bank (optional)" />
      </div>
    </EditModal>
  )
}

// =============================================================================
// EDIT PERSONAL INFO MODAL
// =============================================================================

export function EditPersonalInfoModal({ isOpen, onClose, employee, onSave, isSaving }: { isOpen: boolean; onClose: () => void; employee: Employee; onSave: (data: Partial<Employee>) => void; isSaving?: boolean }) {
  const [dateOfBirth, setDateOfBirth] = useState(employee.dateOfBirth || '')
  const [gender, setGender] = useState(employee.gender || '')
  const genderOpts = [{ value: '', label: 'Prefer not to say' }, { value: 'male', label: 'Male' }, { value: 'female', label: 'Female' }, { value: 'non-binary', label: 'Non-binary' }, { value: 'other', label: 'Other' }]
  return (
    <EditModal isOpen={isOpen} onClose={onClose} title="Edit Personal Information" icon={UserCircle} onSave={() => onSave({ dateOfBirth: dateOfBirth || undefined, gender: gender || undefined })} isSaving={isSaving}>
      <div className="space-y-4">
        <FormInput label="Date of Birth" value={dateOfBirth} onChange={setDateOfBirth} type="date" placeholder="YYYY-MM-DD" />
        <FormSelect label="Gender" value={gender} onChange={setGender} options={genderOpts} placeholder="Select gender (optional)" />
      </div>
    </EditModal>
  )
}
